/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import {
  Button,
  Select,
  Form,
  Row,
  Col,
  InputNumber,
  Statistic,
  Modal,
  Result,
  Tag,
  Table,
  Space,
  Input,
  DatePicker,
  Popconfirm,
} from "antd";
import { ChevronLeft, CheckCircle2, Mail, Trash2, Edit2 } from "lucide-react";
import { DeleteOutlined, PlusOutlined, CheckCircleOutlined, ArrowRightOutlined, ArrowLeftOutlined, WarningOutlined } from "@ant-design/icons";
import CountUp from "react-countup";
import dayjs from "dayjs";

import useTransfer from "../../hooks/useTransfer";
import usePassengers from "../../hooks/usePassengers";
import useParseString from "../../hooks/useParseString";
import BusPaymentFormModal from "../BusPaymentFormModal";

/**
 * DebtDetailContent - Reusable component for displaying and managing debt details
 *
 * @param {Object} props
 * @param {string} props.userId - User ID
 * @param {string} props.type - Type (USER_SELLER, USER_GUIDE, USER_COORDINATOR, etc.)
 * @param {string} props.extraInfo - Extra info for the debt query
 * @param {string} props.transferId - Optional transfer ID for editing existing transfers
 * @param {function} props.onSuccess - Callback when transfer is completed successfully
 * @param {function} props.onCancel - Callback to cancel/go back
 * @param {boolean} props.isModal - If true, adjusts UI for modal display (no back button)
 */
function DebtDetailContent({
  userId,
  type,
  extraInfo,
  transferId,
  onSuccess,
  onCancel,
  isModal = false,
}) {
  const {
    confirmTransfer,
    getTransferDetail,
    updateTransfer,
    transfer,
    getDebtDetail,
    createTransferExpense,
    getSellerExpenses,
    createSellerExpense,
    updateSellerExpense,
    deleteSellerExpense,
    loading,
  } = useTransfer();

  const { paymentMethods, getPaymentMethods } = usePassengers();
  const { formatCurrency } = useParseString();

  const [totalPaid, setTotalPaid] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isBusPaymentModalOpen, setIsBusPaymentModalOpen] = useState(false);
  const [paymentToEdit, setPaymentToEdit] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfObjectUrl, setPdfObjectUrl] = useState(null);
  const [formattedDate, setFormattedDate] = useState(null);
  const [paymentDates, setPaymentDates] = useState({});
  const [successModal, setSuccessModal] = useState({ open: false, sellerName: "" });
  // Estado para gastos de vendedor
  const [sellerExpenses, setSellerExpenses] = useState([]);
  const [expenseModal, setExpenseModal] = useState({ open: false, expense: null });
  const [expenseForm] = Form.useForm();

  // Cleanup object URL when component unmounts
  useEffect(() => {
    return () => {
      if (pdfObjectUrl) {
        URL.revokeObjectURL(pdfObjectUrl);
      }
    };
  }, [pdfObjectUrl]);

  const [form] = Form.useForm();

  const officePayments = Form.useWatch("officePayments", form);

  useEffect(() => {
    getPaymentMethods();
  }, []);

  useEffect(() => {
    if (!userId && !type && !transferId) return;

    getDetail();
  }, [userId, type, extraInfo, transferId]);

  // Cargar gastos del vendedor cuando type es USER_SELLER y no hay transferId
  useEffect(() => {
    if (type === "USER_SELLER" && userId && extraInfo && !transferId) {
      loadSellerExpenses();
    }
  }, [type, userId, extraInfo, transferId]);

  const loadSellerExpenses = async () => {
    const result = await getSellerExpenses({
      seller: userId,
      date: extraInfo,
      unlinked: "true",
    });
    // La API devuelve un objeto paginado con { results: [...] }
    if (result?.results) {
      setSellerExpenses(result.results);
    } else if (Array.isArray(result)) {
      setSellerExpenses(result);
    }
  };

  useEffect(() => {
    if (!transfer) return;

    // Format creation date if available
    if (transfer.resume?.createdAt) {
      try {
        setFormattedDate(
          dayjs(transfer.resume.createdAt).format("DD/MM/YYYY HH:mm")
        );
      } catch (error) {
        console.error("Error formatting date:", error);
        setFormattedDate(null);
      }
    } else {
      setFormattedDate(null);
    }

    // Handle PDF URL if available
    if (transfer.resume?.pdfUrl) {
      setPdfUrl(transfer.resume.pdfUrl);

      // Fetch the PDF blob and create an object URL
      fetch(transfer.resume.pdfUrl)
        .then((response) => response.blob())
        .then((blob) => {
          const objectUrl = URL.createObjectURL(blob);
          setPdfObjectUrl(objectUrl);
        })
        .catch((error) => {
          console.error("Error fetching PDF:", error);
          setPdfObjectUrl(null);
        });
    } else {
      setPdfUrl(null);
      setPdfObjectUrl(null);
    }

    const officePayments = [];
    const dates = {};

    transfer.resume?.payments?.forEach((payment) => {
      // Store payment dates if available
      if (payment.id && payment.createdAt) {
        dates[payment.id] = dayjs(payment.createdAt).format("DD/MM/YYYY HH:mm");
      }

      if (payment.paymentMethod) {
        officePayments.push({
          id: payment.id,
          paymentMethod: payment.paymentMethod.id,
          amount: payment.amount,
        });
      }
    });

    setPaymentDates(dates);
    form.setFieldsValue({ officePayments });
  }, [transfer]);

  useEffect(() => {
    calculateTotalPaid(officePayments);
  }, [officePayments]);

  const getDetail = () => {
    if (transferId) {
      getTransferDetail(transferId);
    } else {
      getDebtDetail({ id: userId, type, extraInfo });
    }
  };

  const calculateTotalPaid = (officePayments) => {
    let total = 0;

    if (officePayments) {
      total += officePayments.reduce(
        (sum, payment) => sum + (payment?.amount || 0),
        0
      );
    }

    setTotalPaid(total);
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleOk = async () => {
    setIsModalVisible(false);
    form.submit();
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const onFinish = async (values) => {
    const payments = [];

    values.officePayments?.forEach((payment) => {
      payments.push({
        id: payment.id,
        paymentMethodId: payment.paymentMethod,
        amount: payment.amount,
      });
    });

    const result = transferId
      ? await updateTransfer(transferId, { payments })
      : await confirmTransfer({ ...transfer.resume, payments });

    // Show success modal for USER_SELLER, otherwise just call onSuccess
    if (result && type === "USER_SELLER") {
      setSuccessModal({
        open: true,
        sellerName: transfer?.resume?.title || transfer?.resume?.sender?.name || "el vendedor"
      });
    } else if (onSuccess) {
      onSuccess();
    }
  };

  const formatter = (value) => (
    <CountUp duration={0.5} end={value} prefix="$" separator="," />
  );

  const handleOpenPaymentModal = () => {
    setIsBusPaymentModalOpen(true);
    setPaymentToEdit(null);
  };

  const handleCancelPaymentModal = () => {
    setIsBusPaymentModalOpen(false);
    setPaymentToEdit(null);
  };

  const handleSuccessPaymentModal = async (payment) => {
    const user =
      transfer.resume.totalAmount > 0
        ? transfer.resume.sender.id
        : transfer.resume.receiver.id;

    await createTransferExpense({ ...payment, user });

    getDetail();

    setIsBusPaymentModalOpen(false);
    setPaymentToEdit(null);
  };

  // Handlers para gastos de vendedor
  const handleOpenExpenseModal = (expense = null) => {
    if (expense) {
      expenseForm.setFieldsValue({
        date: expense.date ? dayjs(expense.date) : null,
        description: expense.description,
        amount: expense.amount,
      });
    } else {
      expenseForm.setFieldsValue({
        date: extraInfo ? dayjs(extraInfo) : dayjs(),
        description: "",
        amount: null,
      });
    }
    setExpenseModal({ open: true, expense });
  };

  const handleCloseExpenseModal = () => {
    setExpenseModal({ open: false, expense: null });
    expenseForm.resetFields();
  };

  const handleSaveExpense = async (values) => {
    const expenseData = {
      seller: userId,
      date: values.date?.format("YYYY-MM-DD"),
      description: values.description,
      amount: values.amount,
    };

    let result;
    if (expenseModal.expense) {
      result = await updateSellerExpense(expenseModal.expense.id, expenseData);
    } else {
      result = await createSellerExpense(expenseData);
    }

    if (result?.id) {
      handleCloseExpenseModal();
      loadSellerExpenses();
      getDetail(); // Refrescar datos para recalcular totales
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    const result = await deleteSellerExpense(expenseId);
    if (result) {
      loadSellerExpenses();
      getDetail(); // Refrescar datos para recalcular totales
    }
  };

  return (
    <>
      {/* Back button - only show if not in modal */}
      {!isModal && (
        <Button
          type="text"
          icon={<ChevronLeft size={16} />}
          onClick={onCancel}
        >
          Volver
        </Button>
      )}

      {formattedDate && (
        <div className="w-full text-center mb-4">
          <span className="text-gray-600">
            Fecha de creación:{" "}
            <span className="font-medium">{formattedDate}</span>
          </span>
        </div>
      )}

      <Row gutter={24} className="mt-4">
        {/* Columna izquierda: PDF */}
        {pdfObjectUrl && (
          <Col span={12}>
            <iframe
              src={pdfObjectUrl}
              style={{
                height: "80vh",
                width: "100%",
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
              }}
              title="Transfer Receipt"
            ></iframe>
          </Col>
        )}

        {/* Columna derecha: Pagos */}
        <Col span={pdfObjectUrl ? 12 : 24}>
          {/* Resumen de deuda */}
          {transfer && transfer.resume && (() => {
            const isFullyPaid = totalPaid >= Math.abs(transfer.resume.totalAmount);
            const mustRender = transfer.resume.totalAmount > 0;
            // Calcular el monto pendiente (falta pagar)
            const pendingAmount = Math.abs(
              transfer.resume.totalAmount > 0
                ? transfer.resume.totalAmount - totalPaid
                : transfer.resume.totalAmount + totalPaid
            );

            return (
              <div className={`mb-4 p-4 rounded-lg border-2 ${
                isFullyPaid
                  ? 'bg-green-50 border-green-200'
                  : mustRender
                    ? 'bg-orange-50 border-orange-200'
                    : 'bg-blue-50 border-blue-200'
              }`}>
                <div className="text-center">
                  {/* Tag de estado */}
                  <div className="mb-3">
                    {isFullyPaid ? (
                      <Tag color="green" className="text-sm px-3 py-1">
                        <CheckCircleOutlined className="mr-1" /> SALDADO
                      </Tag>
                    ) : mustRender ? (
                      <Tag color="orange" className="text-sm px-3 py-1">
                        <ArrowRightOutlined className="mr-1" /> DEBE RENDIR
                      </Tag>
                    ) : (
                      <Tag color="blue" className="text-sm px-3 py-1">
                        <ArrowLeftOutlined className="mr-1" /> A RENDIRLE
                      </Tag>
                    )}
                  </div>

                  {/* Mensaje descriptivo */}
                  <div className="text-lg font-bold mb-2">
                    {isFullyPaid ? (
                      <span className="text-green-600">
                        {mustRender
                          ? `${transfer.resume.sender?.name} completó la rendición`
                          : `Se rindió a ${transfer.resume.receiver?.name}`
                        }
                      </span>
                    ) : mustRender ? (
                      <span className="text-orange-600">
                        {transfer.resume.sender?.name} debe rendir
                      </span>
                    ) : (
                      <span className="text-blue-600">
                        A rendirle a {transfer.resume.receiver?.name}
                      </span>
                    )}
                  </div>

                  {/* Monto - Ahora muestra "Falta pagar" en lugar del total */}
                  <div className={`text-3xl font-bold ${
                    isFullyPaid ? 'text-green-600' : mustRender ? 'text-orange-600' : 'text-blue-600'
                  }`}>
                    {formatCurrency(pendingAmount)}
                  </div>

                  {/* Dirección del flujo */}
                  <div className="text-sm text-gray-500 mt-2 flex items-center justify-center gap-2">
                    {mustRender ? (
                      <>
                        <span>{transfer.resume.sender?.name}</span>
                        <ArrowRightOutlined />
                        <span className="font-medium">Oficina</span>
                      </>
                    ) : (
                      <>
                        <span className="font-medium">Oficina</span>
                        <ArrowRightOutlined />
                        <span>{transfer.resume.receiver?.name}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
          >
            <div className="w-full text-center mb-3">
              <span className="font-semibold text-md">Pagos oficina</span>
            </div>

            <Form.List name="officePayments">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => {
                  const paymentId = form.getFieldValue(["officePayments", name, "id"]);
                  const paymentData = transfer?.resume?.payments?.find(p => p.id === paymentId);
                  const createdByInfo = paymentData?.createdBy;
                  const receiverName = transfer?.resume?.receiver?.name;

                  return (
                    <div key={key}>
                      {/* Mostrar flujo del pago si existe createdBy */}
                      {createdByInfo && (
                        <div className="mb-2 text-sm text-gray-600 bg-gray-50 p-2 rounded-md flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-blue-600">
                            {createdByInfo.name}
                          </span>
                          <span className="text-gray-400">({createdByInfo.typeDisplay})</span>
                          <ArrowRightOutlined className="text-gray-400" />
                          <span className="text-gray-500">
                            {paymentData?.paymentMethod?.box || paymentData?.paymentMethod?.name}
                          </span>
                          <span className="font-semibold text-green-600">
                            {formatCurrency(paymentData?.amount || 0)}
                          </span>
                          <ArrowRightOutlined className="text-gray-400" />
                          <span className="font-medium text-purple-600">
                            {receiverName}
                          </span>
                        </div>
                      )}
                      <Row gutter={8} align="middle">
                        <Col span={11}>
                          <Form.Item
                            {...restField}
                            name={[name, "paymentMethod"]}
                          >
                            <Select
                              placeholder="Seleccione un método de pago"
                              popupMatchSelectWidth={false}
                              labelRender={(label) => {
                                if (!label) return;

                                const paymentMethod = paymentMethods?.find(
                                  (pm) => {
                                    return pm.id === label.value;
                                  }
                                );

                                if (!paymentMethod) return;

                                return `${paymentMethod.name} - ${paymentMethod.box}`;
                              }}
                            >
                              {paymentMethods?.map((method) => (
                                <Select.Option key={method.id} value={method.id}>
                                  {method.name} - {method.box}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={11}>
                          <div className="flex items-center gap-2">
                            {paymentId &&
                              paymentDates[paymentId] && (
                                <div className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-md whitespace-nowrap mb-6">
                                  <CheckCircleOutlined />
                                  <span className="font-medium">
                                    {paymentDates[paymentId]}
                                  </span>
                                </div>
                              )}
                            <Form.Item
                              {...restField}
                              name={[name, "amount"]}
                              rules={[
                                { required: true, message: "Ingrese un monto" },
                              ]}
                              className="flex-1 mb-6"
                            >
                              <InputNumber
                                placeholder="Monto"
                                className="w-full"
                                formatter={(value) =>
                                  `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                }
                                parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                              />
                            </Form.Item>
                          </div>
                        </Col>
                        <Col span={2}>
                          <DeleteOutlined
                            className="text-red-600 mb-[30px]"
                            onClick={() => {
                              remove(name);
                            }}
                          />
                        </Col>
                      </Row>
                    </div>
                  );
                })}
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                  >
                    Agregar Pago oficina
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>

          {transfer && transfer.resume && (
            <Row gutter={16} className="mt-4">
              <Col span={8}>
                <Statistic
                  title="Total a rendir"
                  value={Math.abs(transfer.resume.totalAmount)}
                  formatter={formatter}
                  valueStyle={{ color: "#666" }}
                />
              </Col>

              <Col span={8}>
                <Statistic
                  title="Ya pagado"
                  value={totalPaid}
                  formatter={formatter}
                  valueStyle={{ color: "#3f8600" }}
                />
              </Col>

              <Col span={8}>
                <Statistic
                  title="Falta pagar"
                  value={Math.abs(
                    transfer.resume.totalAmount > 0
                      ? transfer.resume.totalAmount - totalPaid
                      : transfer.resume.totalAmount + totalPaid
                  )}
                  formatter={formatter}
                  valueStyle={{ color: "#cf1322" }}
                />
              </Col>
            </Row>
          )}

          {/* Sección de gastos del vendedor - solo para USER_SELLER */}
          {type === "USER_SELLER" && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
              <div className="flex justify-between items-center mb-3">
                <span className="font-semibold text-md">Gastos del Vendedor</span>
                {transferId ? (
                  <Tag color="warning" icon={<WarningOutlined />}>
                    La deuda ya fue creada, no se pueden añadir más gastos
                  </Tag>
                ) : (
                  <Button
                    size="small"
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => handleOpenExpenseModal()}
                  >
                    Agregar Gasto
                  </Button>
                )}
              </div>
              <Table
                dataSource={sellerExpenses}
                rowKey="id"
                size="small"
                pagination={false}
                locale={{ emptyText: "No hay gastos registrados" }}
                columns={[
                  {
                    title: "Fecha",
                    dataIndex: "date",
                    key: "date",
                    width: 100,
                    render: (date) => dayjs(date).format("DD/MM/YYYY"),
                  },
                  {
                    title: "Descripción",
                    dataIndex: "description",
                    key: "description",
                  },
                  {
                    title: "Monto",
                    dataIndex: "amount",
                    key: "amount",
                    width: 120,
                    align: "right",
                    render: (amount) => (
                      <span className="text-red-600 font-medium">
                        {formatCurrency(amount)}
                      </span>
                    ),
                  },
                  // Solo mostrar acciones si NO hay transferId
                  ...(!transferId
                    ? [
                        {
                          title: "Acciones",
                          key: "actions",
                          width: 100,
                          render: (_, record) => (
                            <Space size="small">
                              <Button
                                type="text"
                                size="small"
                                icon={<Edit2 size={14} />}
                                onClick={() => handleOpenExpenseModal(record)}
                              />
                              <Popconfirm
                                title="¿Eliminar gasto?"
                                description="Esta acción no se puede deshacer"
                                onConfirm={() => handleDeleteExpense(record.id)}
                                okText="Sí"
                                cancelText="No"
                                okButtonProps={{ danger: true }}
                              >
                                <Button
                                  type="text"
                                  size="small"
                                  danger
                                  icon={<Trash2 size={14} />}
                                />
                              </Popconfirm>
                            </Space>
                          ),
                        },
                      ]
                    : []),
                ]}
              />
              {sellerExpenses.length > 0 && (
                <div className="mt-2 text-right">
                  <span className="text-gray-500">Total gastos: </span>
                  <span className="font-semibold text-red-600">
                    {formatCurrency(
                      sellerExpenses.reduce((sum, e) => sum + (e.amount || 0), 0)
                    )}
                  </span>
                </div>
              )}
            </div>
          )}

          <Button
            htmlType="button"
            type="primary"
            loading={loading.post}
            className="mt-4"
            onClick={showModal}
          >
            {transferId ? "Modificar transferencia" : "Rendir transferencia"}
          </Button>

          <Modal
              title="Confirmar Rendición"
              open={isModalVisible}
              onOk={handleOk}
              onCancel={handleCancel}
              okText="Confirmar"
              cancelText="Cancelar"
            >
              {transfer && transfer.resume && (
                <div className="py-4">
                  <div className="text-center mb-4">
                    <div className="text-gray-500 text-sm mb-1">Monto a registrar</div>
                    <div className="text-3xl font-bold text-green-600">
                      {formatCurrency(totalPaid)}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="text-center flex-1">
                        <div className="text-xs text-gray-400 mb-1">De</div>
                        <div className="font-semibold text-lg">
                          {transfer.resume.sender?.name}
                        </div>
                      </div>

                      <div className="px-4">
                        <div className="text-2xl text-gray-400">→</div>
                      </div>

                      <div className="text-center flex-1">
                        <div className="text-xs text-gray-400 mb-1">Para</div>
                        <div className="font-semibold text-lg">
                          {transfer.resume.receiver?.name}
                        </div>
                      </div>
                    </div>
                  </div>

                  {Math.abs(
                    transfer.resume.totalAmount > 0
                      ? transfer.resume.totalAmount - totalPaid
                      : transfer.resume.totalAmount + totalPaid
                  ) > 0 && (
                    <div className="mt-4 text-center text-sm text-orange-600">
                      Quedará un saldo pendiente de{" "}
                      <span className="font-semibold">
                        {formatCurrency(Math.abs(
                          transfer.resume.totalAmount > 0
                            ? transfer.resume.totalAmount - totalPaid
                            : transfer.resume.totalAmount + totalPaid
                        ))}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </Modal>
          </Form>
        </Col>
      </Row>
      <BusPaymentFormModal
        isOpen={isBusPaymentModalOpen}
        onCancel={handleCancelPaymentModal}
        paymentToEdit={paymentToEdit}
        onSuccess={handleSuccessPaymentModal}
      />

      {/* Success Modal for USER_SELLER */}
      <Modal
        open={successModal.open}
        footer={null}
        onCancel={() => {
          setSuccessModal({ open: false, sellerName: "" });
          if (onSuccess) onSuccess();
        }}
        centered
        width={420}
        closable={false}
      >
        <Result
          icon={
            <div className="flex flex-col items-center gap-3">
              <CheckCircle2 size={56} className="text-green-500" />
              <Mail size={36} className="text-blue-500" />
            </div>
          }
          title="¡Pago registrado!"
          subTitle={`Se envió un mail automático a ${successModal.sellerName} con el comprobante de la rendición.`}
          extra={
            <Button
              type="primary"
              size="large"
              onClick={() => {
                setSuccessModal({ open: false, sellerName: "" });
                if (onSuccess) onSuccess();
              }}
            >
              Aceptar
            </Button>
          }
        />
      </Modal>

      {/* Modal para crear/editar gasto de vendedor */}
      <Modal
        title={expenseModal.expense ? "Editar Gasto" : "Nuevo Gasto"}
        open={expenseModal.open}
        onCancel={handleCloseExpenseModal}
        onOk={() => expenseForm.submit()}
        confirmLoading={loading.post || loading.put}
        okText="Guardar"
        cancelText="Cancelar"
      >
        <Form
          form={expenseForm}
          layout="vertical"
          onFinish={handleSaveExpense}
        >
          <Form.Item
            name="date"
            label="Fecha"
            rules={[{ required: true, message: "Selecciona la fecha" }]}
          >
            <DatePicker
              format="DD/MM/YYYY"
              style={{ width: "100%" }}
              placeholder="Seleccionar fecha"
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Descripción"
            rules={[{ required: true, message: "Ingresa una descripción" }]}
          >
            <Input placeholder="Ej: Combustible, almuerzo, etc." />
          </Form.Item>

          <Form.Item
            name="amount"
            label="Monto"
            rules={[{ required: true, message: "Ingresa el monto" }]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              placeholder="Ej: 5000"
              formatter={(value) =>
                `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default DebtDetailContent;
